// useSessionHistory.ts — loads the selected session's transcript.
//
// History comes from the harness whenever the selection changes, with or
// without the workspace's Pod. A refresh mid-turn shows whatever the
// harness had committed at the moment of the GET.
//
// Selecting a session is also what opens it: POST /sessions/{id}/open
// starts the workspace's Pod in the background. That covers every way a
// session becomes selected — a click, a create, a fork, the first load.
//
// `historyLoadedId` is what gates the session's socket onto the
// transcript (see focusedSessionId in turnState.ts): until the history is
// on screen, frames from the socket would be written into the previous
// session's conversation, or be overwritten by the history landing.
import React, { useEffect, useState } from 'react';
import { sessionsApi } from '../api';
import type { SessionMessage } from '../api';
import { resetCursor } from './live/sessionLive';

export interface SessionHistoryArgs {
  selectedId: string | null;
  setMessages: React.Dispatch<React.SetStateAction<SessionMessage[]>>;
  setStreamError: (msg: string) => void;
}

export interface SessionHistory {
  /** The session whose transcript is on screen, as opposed to selected. */
  historyLoadedId: string | null;
  loadingSession: boolean;
}

export function useSessionHistory({
  selectedId, setMessages, setStreamError,
}: SessionHistoryArgs): SessionHistory {
  const [historyLoadedId, setHistoryLoadedId] = useState<string | null>(null);
  const [loadingSession, setLoadingSession] = useState(false);

  useEffect(() => {
    setHistoryLoadedId(null);
    // Cleared at once rather than when the new history lands: until then
    // the pane would show the previous session's conversation under the
    // newly selected one's banner.
    setMessages([]);
    setStreamError('');
    if (!selectedId) return;
    // Fire and forget: the open answers before the Pod is up, and a failed
    // one costs nothing the chat path's own wake would not retry.
    sessionsApi.open(selectedId).catch((err) => {
      console.debug('[sessions] open failed', { session: selectedId, err: String(err) });
    });
    let alive = true;
    setLoadingSession(true);
    (async () => {
      try {
        const s = await sessionsApi.get(selectedId);
        if (!alive) return;
        const msgs = await sessionsApi.history(s);
        if (!alive) return;
        console.debug('[sessions] history loaded', { session: selectedId, messages: msgs.length });
        setMessages(msgs);
        setStreamError('');
        // A transcript fetched mid-turn stops at the user's message — the
        // harness commits the reply only when the turn ends — so the
        // socket has to replay that turn from its start rather than resume
        // from wherever this page had got to before.
        resetCursor(selectedId);
        setHistoryLoadedId(selectedId);
      } catch (err) {
        if (alive) setStreamError(String(err));
      } finally {
        if (alive) setLoadingSession(false);
      }
    })();
    return () => { alive = false; };
    // The setters are stable; keying on the selection alone is
    // what makes this a load-per-selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  return { historyLoadedId, loadingSession };
}
